import React, { useMemo, useRef } from "react";

import LogoutIcon from "@mui/icons-material/Logout";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import RemoveOutlinedIcon from "@mui/icons-material/RemoveOutlined";
import TimelineIcon from "@mui/icons-material/Timeline";
import { Box, Button, Link as MuiLink, Popover } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";

import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ButtonVariant } from "Constant/button";
import diffHistory from "Constant/diff.json";
import { routes } from "Constant/routes";
import Notification from "pages/Notifications/NotificationBell";
import Collapse from "react-bootstrap/Collapse";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { MenuRow, popoverMenuPaperSx } from "components/atoms/PopoverMenu";
import StandartButton from "components/atoms/StandartButton";

import { createSidebarItems } from "../SidebarManager.helpers";
import {
    BottomArea,
    Container,
    LogoWrap,
    MainMenuList,
    MenuGroupItem,
    MenuGroupTrigger,
    MenuItem,
    MenuLabel,
    MenuLink,
    Panel,
    ScrollArea,
    SideNav,
    SubmenuList,
    WizardButtonBottom,
    WizardExitButton,
    WizardTextArea,
    WizardTitle,
} from "./SidebarUI.styled";

const latestVersion = diffHistory[diffHistory.length - 1]?.version;

const SidebarUI = ({
    language,
    trackersStatus,
    trackers,
    shouldShowExitButton,
    bussinessName,
    onLogout,
    isAccountDropdownOpen,
    setIsAccountDropdownOpen,

    dispatchSetSidebarItem,
    landingPageService,
    leadService,
    setIsLandingPageModalOpen,
    setIsLeadModalOpen,
    sidebarOpenedItem,
    signalService,
    setActiveItem,
    activeItem,
    isSidebarOpen,
}) => {
    const accountButtonRef = useRef();

    const location = useLocation();
    const navigate = useNavigate();
    const isMobileSidebar = useMediaQuery("(max-width: 991px)");

    const isTrackerWizard = location.pathname === "/wizard";
    const isLeadsWizard = location.pathname === "/leads-wizard";

    const shouldShowHeaderBurger = useMemo(() => {
        const handlers = [
            {
                predicate: () => location.pathname === "/leads-wizard",
                handler: () => false,
            },
            {
                predicate: () => location.pathname === "/wizard",
                handler: () => false,
            },
            {
                predicate: () => true,
                handler: () => true,
            },
        ];
        return handlers?.find(({ predicate }) => predicate())?.handler();
    }, [location.pathname]);
    return (
        <Container
            isSidebarOpen={isSidebarOpen}
            shouldShowHeaderBurger={shouldShowHeaderBurger}
            isWizardRoute={isTrackerWizard || isLeadsWizard}
        >
            {isTrackerWizard || isLeadsWizard ? (
                <Panel
                    isHidden={
                        (isTrackerWizard && location.hash === "#step4") ||
                        (isLeadsWizard && location.hash === "#step5")
                    }
                >
                    <LogoWrap>
                        <Link to={routes.home}>
                            <img
                                style={{ width: "180px" }}
                                src="/assets/images/signal-sight.png"
                                alt="SignalSight Logo"
                            />
                        </Link>
                    </LogoWrap>
                    <WizardTextArea>
                        <WizardTitle>
                            {isLeadsWizard
                                ? (language?.lead_wizard?.title ??
                                  "Leads Wizard")
                                : (language?.tracker_wizard?.title ??
                                  "Tracker Wizard")}
                        </WizardTitle>
                        <p>
                            <strong>
                                {language?.tracker_wizard
                                    ?.why_do_id_need_fill_these ??
                                    "Why do I need to complete these steps?"}
                            </strong>
                        </p>
                        <img
                            src={
                                isLeadsWizard
                                    ? "/assets/images/theme-v2-sidebar-vector-lead.png"
                                    : "/assets/images/theme-v2-sidebar-vector.png"
                            }
                            alt="#"
                        />

                        <p>
                            {isLeadsWizard
                                ? (language?.lead_wizard
                                      ?.there_will_be_a_minimum_of_2_node_points_to_connect_with ??
                                  "There will be a minimum of 2 node points to connect with.")
                                : (language?.tracker_wizard
                                      ?.there_will_be_a_minimum_of_2_node_points_to_connect_with ??
                                  "There will be a minimum of 2 node points to connect with.")}
                        </p>
                        <p
                            dangerouslySetInnerHTML={{
                                __html: isLeadsWizard
                                    ? (language?.lead_wizard?.we_call_them ??
                                      "We call them <b>Source</b> and <b>Destination</b>.")
                                    : (language?.tracker_wizard?.we_call_them ??
                                      "These are referred to as the <b>Source</b> and the <b>Destination</b>."),
                            }}
                        ></p>
                        <p
                            dangerouslySetInnerHTML={{
                                __html: isLeadsWizard
                                    ? (language?.lead_wizard
                                          ?.then_you_need_to_name_this_connection ??
                                      "Then, you need to name this connection <b>Connection</b>, so you can manage the path if you want to.")
                                    : (language?.tracker_wizard
                                          ?.then_you_need_to_name_this_connection ??
                                      "You should assign a unique name to each connection you create. This allows for easier management and identification of the path as needed."),
                            }}
                        ></p>
                    </WizardTextArea>
                    <WizardButtonBottom>
                        {(isLeadsWizard || shouldShowExitButton) && (
                            <WizardExitButton
                                variant="outlined"
                                color="inherit"
                                onClick={() =>
                                    navigate(
                                        isLeadsWizard
                                            ? "/lead-connections"
                                            : "/trackers",
                                    )
                                }
                                fullWidth
                                size="large"
                            >
                                {language?.tracker_wizard?.exit_wizard ??
                                    "EXIT WIZARD"}
                            </WizardExitButton>
                        )}
                    </WizardButtonBottom>
                </Panel>
            ) : (
                <Panel>
                    <LogoWrap>
                        <Link to={routes.home}>
                            <img
                                style={{ width: "180px" }}
                                src="/assets/images/signal-sight.png"
                                alt="SignalSight Logo"
                            />
                        </Link>
                    </LogoWrap>
                    <ScrollArea>
                        <SideNav>
                            <MainMenuList id="menu">
                                {createSidebarItems({
                                    dispatchSetSidebarItem,
                                    landingPageService,
                                    language,
                                    leadService,
                                    setIsLandingPageModalOpen,
                                    setIsLeadModalOpen,
                                    sidebarOpenedItem,
                                    signalService,
                                }).map((parent) => {
                                    if (!parent?.children) {
                                        return (
                                            <MenuItem key={parent.route}>
                                                <MenuLink
                                                    to={parent.route}
                                                    data-cy={parent.testId}
                                                    data-active={
                                                        activeItem ===
                                                        parent?.route
                                                    }
                                                >
                                                    {parent.icon}
                                                    <MenuLabel>
                                                        {parent.title}
                                                    </MenuLabel>
                                                </MenuLink>
                                            </MenuItem>
                                        );
                                    }
                                    return (
                                        <MenuGroupItem key={parent.itemKey}>
                                            <MenuGroupTrigger
                                                data-cy={parent.testId}
                                                onClick={parent.onClick}
                                                aria-controls="businessDropdown"
                                                aria-expanded={
                                                    sidebarOpenedItem ===
                                                    parent?.itemKey
                                                }
                                                data-open={
                                                    sidebarOpenedItem ===
                                                    parent?.itemKey
                                                }
                                            >
                                                {parent.icon}
                                                {parent.title}
                                                <FontAwesomeIcon
                                                    icon={faChevronDown}
                                                    className="pl-4"
                                                />
                                            </MenuGroupTrigger>
                                            <Collapse
                                                in={
                                                    sidebarOpenedItem ===
                                                    parent.itemKey
                                                }
                                            >
                                                <SubmenuList id="businessDropdown">
                                                    {parent.children?.map(
                                                        ({
                                                            title,
                                                            route,
                                                            icon,
                                                            isExternalRoute,
                                                            testId,
                                                        }) =>
                                                            isExternalRoute ? (
                                                                <li key={route}>
                                                                    <MuiLink
                                                                        data-cy={
                                                                            testId
                                                                        }
                                                                        href={
                                                                            route
                                                                        }
                                                                        underline="none"
                                                                    >
                                                                        {icon}
                                                                        <MenuLabel>
                                                                            {
                                                                                title
                                                                            }
                                                                        </MenuLabel>
                                                                    </MuiLink>
                                                                </li>
                                                            ) : (
                                                                <li
                                                                    key={route}
                                                                    data-active={
                                                                        activeItem ===
                                                                        route
                                                                    }
                                                                >
                                                                    <Link
                                                                        data-cy={
                                                                            testId
                                                                        }
                                                                        to={
                                                                            route
                                                                        }
                                                                    >
                                                                        <RemoveOutlinedIcon></RemoveOutlinedIcon>
                                                                        <MenuLabel>
                                                                            {
                                                                                title
                                                                            }
                                                                        </MenuLabel>
                                                                    </Link>
                                                                </li>
                                                            ),
                                                    )}
                                                </SubmenuList>
                                            </Collapse>
                                        </MenuGroupItem>
                                    );
                                })}
                            </MainMenuList>
                        </SideNav>
                    </ScrollArea>
                    <BottomArea>
                        <StandartButton
                            variant={ButtonVariant.HELP}
                            href="https://signalsight.io/docs/"
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={<MenuBookIcon />}
                            fullWidth
                            sx={{
                                justifyContent: "flex-start",
                                width: "100%",
                                mb: "10px",
                            }}
                        >
                            {language?.buttons?.get_help ?? "Get Help"}
                        </StandartButton>
                        <Notification />
                        <StandartButton
                            variant={ButtonVariant.ACCOUNT}
                            setRef={accountButtonRef}
                            id="accountDropdownButton"
                            aria-controls="accountDropdown"
                            aria-expanded={isAccountDropdownOpen}
                            startIcon={<PersonOutlineOutlinedIcon />}
                            fullWidth
                            onClick={() =>
                                setIsAccountDropdownOpen(!isAccountDropdownOpen)
                            }
                            sx={{
                                justifyContent: "flex-start",
                                width: "100%",
                                mb: "10px",
                                minWidth: 0,
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    minWidth: 0,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {bussinessName}
                            </Box>
                        </StandartButton>
                        <Popover
                            id="accountDropdown"
                            open={isAccountDropdownOpen}
                            anchorEl={accountButtonRef.current}
                            onClose={() => setIsAccountDropdownOpen(false)}
                            anchorOrigin={{
                                vertical: "top",
                                horizontal: isMobileSidebar ? "left" : "right",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: isMobileSidebar ? "right" : "left",
                            }}
                            slotProps={{
                                paper: {
                                    sx: {
                                        ...popoverMenuPaperSx,
                                        mt: 0,
                                        ...(isMobileSidebar
                                            ? { mr: "8px" }
                                            : { ml: "8px" }),
                                    },
                                },
                            }}
                            disableRestoreFocus
                            disableScrollLock
                        >
                            <MenuRow
                                icon={<PersonOutlineOutlinedIcon />}
                                label="Account"
                                onClick={() => {
                                    setIsAccountDropdownOpen(false);
                                    window.open(
                                        "https://console.signalsight.io/dash/subscriptions",
                                        "_blank",
                                    );
                                }}
                            />
                            <MenuRow
                                icon={<TimelineIcon />}
                                label={
                                    language?.sidebar?.activity_log ??
                                    "Activity Log"
                                }
                                onClick={() => {
                                    setIsAccountDropdownOpen(false);
                                    navigate(routes.activity_log);
                                }}
                            />
                            <MenuRow
                                icon={<LogoutIcon />}
                                label={language?.buttons?.log_out ?? "Log out"}
                                onClick={() => {
                                    setIsAccountDropdownOpen(false);
                                    onLogout();
                                }}
                                danger
                                isLast
                            />
                            {latestVersion && (
                                <div
                                    className="version-label"
                                    id="version-app-signalight"
                                    style={{
                                        display: "none",
                                    }}
                                >
                                    {`v${latestVersion}`}
                                </div>
                            )}
                        </Popover>
                    </BottomArea>
                </Panel>
            )}
        </Container>
    );
};

export default SidebarUI;
